// @ai-generated 2026-08-06T09:13:59+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { type ApiContainerAction, type ApiOrder } from '@Model';
import {
    type AsyncStateUpdate,
    Button,
    FlexColumn,
    FlexRow,
    FloatingCard,
    iconSizeXs,
    useEditSubObject,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconChecks } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { ContainerActionButtons } from './ContainerActionButtons';
import { ContainerCorrectionBox } from './ContainerCorrectionBox';
import { type ContainerCorrectionState } from './containerCorrections';
import {
    hasNoContainerType,
    type PlannedContainerGroup,
    removedBookedActions,
    toContainerActionGroups,
} from './containerActionGroups';
import { ContainerEmptyState } from './ContainerEmptyState';
import { poolCapAtSite } from './containerRowState';
import { type ConfirmableLines, confirmableLines } from './lineConfirmation';
import { splitExchanges, toPlannedContainerEntries } from './plannedContainerEntries';
import { PlannedContainerList } from './PlannedContainerList';
import { useContainerLineConfirmations } from './useContainerLineConfirmations';
import { useContainerLineFocus } from './useContainerLineFocus';
import { type ContainerPlanning } from './useContainerPlanning';
import { useContainerMaterial } from './useContainerMaterial';
import { type SiteContainers, useContainerOrderState } from './useContainerOrderState';
import { useContainerTypeChange } from './useContainerTypeChange';
import { useContainerCorrections } from './useContainerCorrections';
import { useContainerTypeDetails } from './useContainerTypeDetails';
import { useOpensOnFirstRow } from './useOpensOnFirstRow';
import { usePlanAndOpen } from './usePlanAndOpen';

interface ContainerCardProps {
    data: ApiOrder;
    apiResourcePath: string;
    canEdit: boolean;
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
    /** Reads the order again, e.g. after the server marked the other half of an exchange. */
    refetchOrder: () => Promise<unknown>;
    /** Plans container actions into the order; shared with the site sidebar so one call runs at a time. */
    planning: ContainerPlanning;
    /** How many containers stand at the site, by type and content. */
    standingCount: ReadonlyMap<string, number>;
    /** What stands and is planned at the order's site; unset while that is not known. */
    site: SiteContainers | undefined;
    /** Whether a placement can be added here; not where the order is shown without its site sidebar. */
    offersPlacement: boolean;
    /** The order as it was when the correction started. Unset outside a correction. */
    atEditStart?: ContainerCorrectionState;
}

/** The container actions planned on an order: one line each, and buttons in the header that add more. */
export function ContainerCard({
    data,
    apiResourcePath,
    canEdit,
    requestUpdateState,
    refetchOrder,
    planning,
    standingCount,
    site,
    offersPlacement,
    atEditStart,
}: ContainerCardProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const canPlan = canEdit && hasPermission(Permission.AuftragsbearbeitungContainerActionsUpdate);
    const canDelete = canEdit && hasPermission(Permission.AuftragsbearbeitungContainerActionsDelete);

    const baseApiObjectPath = `${apiResourcePath}/${data.id}`;
    const containerActions = data.container_actions;

    const { deleteSubObjects, requestUpdateSubObject } = useEditSubObject<ApiContainerAction, ApiOrder>(
        baseApiObjectPath,
        'container_actions',
        requestUpdateState,
    );

    const deleteContainerActions = useCallback(
        (actionIds: string[]) => {
            void deleteSubObjects(actionIds);
        },
        [deleteSubObjects],
    );

    const mergeLinkedAction = useCallback(
        (action: ApiContainerAction) => {
            void requestUpdateSubObject(() => ({ data: action, messages: [] }), action.id);
        },
        [requestUpdateSubObject],
    );

    const canMarkForBooking = canPlan;
    const completing = Boolean(data.completed_at);

    const confirmations = useContainerLineConfirmations(
        data.id,
        mergeLinkedAction,
        requestUpdateState,
        refetchOrder,
        containerActions,
        completing,
    );
    const material = useContainerMaterial(data.id, mergeLinkedAction);
    const typeChange = useContainerTypeChange(data.id, mergeLinkedAction);
    const hasSite = Boolean(data.customer.foreign_key && data.site.address.foreign_key);

    const usesIndividualManagement = useContainerTypeDetails(
        useMemo(() => containerActions.map((action) => action.container_type.foreign_key), [containerActions]),
    );

    const { exchanges, rest } = useMemo(() => splitExchanges(containerActions), [containerActions]);
    const groups = useMemo(() => toContainerActionGroups(rest), [rest]);
    const untyped = useMemo(() => rest.filter(hasNoContainerType), [rest]);

    const entries = useMemo(
        () => toPlannedContainerEntries(groups, usesIndividualManagement, exchanges, untyped),
        [groups, usesIndividualManagement, exchanges, untyped],
    );

    const isIndividual = useCallback(
        (action: ApiContainerAction) =>
            usesIndividualManagement.get(action.container_type.foreign_key ?? '')?.usesIndividualManagement === true,
        [usesIndividualManagement],
    );
    const openLines = useMemo(() => confirmableLines(entries, isIndividual), [entries, isIndividual]);
    const { focusActionId, focusNextPlanned } = useContainerLineFocus(
        containerActions,
        completing,
        planning.isPlanning,
        isIndividual,
    );

    const corrections = useContainerCorrections(data, atEditStart);
    // Most orders plan no container at all, so the card stays closed until one is planned or corrected.
    const [opened, setOpened] = useOpensOnFirstRow(containerActions.length + corrections.length);
    const orderState = useContainerOrderState(data, canEdit, site, Boolean(atEditStart));
    const capOf = useCallback(
        (group: PlannedContainerGroup) => poolCapAtSite(group, containerActions, standingCount),
        [containerActions, standingCount],
    );

    const plan = usePlanAndOpen(planning, focusNextPlanned, setOpened);

    const growGroup = useCallback(
        (group: PlannedContainerGroup, count: number) => {
            const restored = removedBookedActions(group, atEditStart?.actions ?? [], containerActions).slice(0, count);
            if (restored.length > 0) {
                void requestUpdateState((latest, saveData) =>
                    saveData({ ...latest, container_actions: [...latest.container_actions, ...restored] }),
                );
            }

            if (count > restored.length) {
                const row = group.actions[group.actions.length - 1];
                void planning.plan(undefined, { draftContainerActionId: row.id, count: count - restored.length });
            }
        },
        [atEditStart, containerActions, planning, requestUpdateState],
    );

    return (
        <FloatingCard
            title={i18n('Container')}
            opened={opened}
            setOpened={setOpened}
            additionalHeaderItems={
                <FlexRow gap="xs">
                    {canPlan && offersPlacement && (
                        <ContainerActionButtons
                            locked={planning.isPlanning || !hasSite}
                            lockedReason={hasSite ? undefined : i18n('Erst Kunde und Lieferanschrift wählen.')}
                            onPlan={plan}
                        />
                    )}
                    {canMarkForBooking && completing && openLines.openCount >= 2 && (
                        <ConfirmAllButton
                            openLines={openLines}
                            locked={confirmations.isMarking}
                            onConfirmAll={confirmations.confirmAll}
                        />
                    )}
                </FlexRow>
            }
        >
            <FlexColumn gap="sm" align="stretch">
                {containerActions.length === 0 && <ContainerEmptyState />}
                <PlannedContainerList
                    entries={entries}
                    baseApiObjectPath={baseApiObjectPath}
                    canEdit={canEdit}
                    canLink={canPlan}
                    canDelete={canDelete}
                    canMarkForBooking={canMarkForBooking}
                    isMarking={confirmations.isMarking}
                    completing={completing}
                    focusActionId={focusActionId}
                    material={material}
                    typeChange={typeChange}
                    containerTypes={usesIndividualManagement}
                    orderState={orderState}
                    capOf={capOf}
                    requestUpdateAction={requestUpdateSubObject}
                    onDelete={deleteContainerActions}
                    onGrowGroup={canPlan ? growGroup : undefined}
                    growLocked={planning.isPlanning}
                    onMarkForBooking={confirmations.confirm}
                    onMarkGroupForBooking={confirmations.confirmGroup}
                    onMarkExchangeForBooking={confirmations.confirmExchange}
                    onUnmarkForBooking={confirmations.unconfirm}
                    onUnmarkGroupForBooking={confirmations.unconfirmGroup}
                    onUnmarkExchangeForBooking={confirmations.unconfirmExchange}
                />
                <ContainerCorrectionBox corrections={corrections} />
            </FlexColumn>
        </FloatingCard>
    );
}

/** Confirms every open line that is complete, in one go. */
// @ai-generated 2026-10-19T08:21:57+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function ConfirmAllButton({
    openLines,
    locked,
    onConfirmAll,
}: {
    openLines: ConfirmableLines;
    locked: boolean;
    onConfirmAll: (actionIds: string[]) => void;
}) {
    const i18n = useTranslate('');

    return (
        <Button
            appearance="outlined"
            size="xs"
            locked={locked || openLines.confirmableCount === 0}
            onClick={() => onConfirmAll(openLines.actionIds)}
        >
            <IconChecks size={iconSizeXs} />
            &nbsp;{`${i18n('Alle bestätigen')} (${openLines.confirmableCount})`}
        </Button>
    );
}
